/**
 * TopBarContent Context
 *
 * Allows child views to inject content into the AppShell's top bar.
 * Used by ReviewView to render progress indicator in the nav bar.
 */

import { createContext, useContext, useState, useCallback, useEffect } from 'react'

interface TopBarContentContextValue {
  content: React.ReactNode | null
  setContent: (content: React.ReactNode | null) => void
}

const TopBarContentContext = createContext<TopBarContentContextValue>({
  content: null,
  setContent: () => {},
})

/**
 * Provider component for top bar content injection.
 * Wrap AppShell children with this.
 */
export function TopBarContentProvider({ children }: { children: React.ReactNode }) {
  const [content, setContent] = useState<React.ReactNode | null>(null)

  return (
    <TopBarContentContext.Provider value={{ content, setContent }}>
      {children}
    </TopBarContentContext.Provider>
  )
}

/**
 * Hook for child views to inject content into the top bar.
 * Automatically clears on unmount.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useTopBarContent() {
  const { setContent } = useContext(TopBarContentContext)

  useEffect(() => {
    return () => {
      setContent(null)
    }
  }, [setContent])

  const setTopBarContent = useCallback((node: React.ReactNode | null) => {
    setContent(node)
  }, [setContent])

  return { setTopBarContent }
}

/**
 * Hook for AppShell to read the injected top bar content.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useTopBarContentValue() {
  const { content } = useContext(TopBarContentContext)
  return content
}
